import React, { useEffect, useState } from 'react';
import { createResource,getResources,getResourceById, updateResource} from '../../../router/api';
import toast from 'react-hot-toast';

// Change cette URL si ton endpoint clients est différent
const CLIENTS_API = `clients`;

interface CompteBancaire {
  id: number;
  nom: string;
  banque: string;
  numero_compte: string;
  type: 'courant' | 'epargne' | 'credit' | null;
  solde_theorique: number;
}

interface Client {
  id: number;
  nom: string;
  // Ajoute d'autres champs si besoin : telephone, email, etc.
}

interface OperationBancaire {
  id: number;
  compte_bancaire_id: number;
  sens: 'ENTRANT' | 'SORTANT';
  montant: number;
  description: string | null;
  reference: string | null;
  date_virement: string;
  statut: 'EXECUTE' | 'EN_ATTENTE' | 'ANNULE';
}

interface RemiseCheque {
  id: number;
  compte_bancaire_id: number;
  numero_cheque: string;
  montant: number;
  client_id: string;
  date_remise_prevue: string;
  statut: 'EN_ATTENTE' | 'ENCAISSE' | 'REJETE';
}

// Ajoute ces interfaces en haut avec les autres
interface RapprochementBancaire {
  id: number;
  compte_bancaire_id: number;
  periode_debut: string;
  periode_fin: string;
  solde_theorique: number;
  solde_releve: number;
  ecart: number;
  observations: string | null;
  created_at: string;
}

export default function BankManagement() {
  const [activeTab, setActiveTab] = useState<'accounts' | 'operations' | 'checks' | 'reconciliation'>('accounts');
  const [comptes, setComptes] = useState<CompteBancaire[]>([]);
  const [operations, setOperations] = useState<OperationBancaire[]>([]);
  const [cheques, setCheques] = useState<RemiseCheque[]>([]);
  const [clients, setClients] = useState<Client[]>([]); // Nouveau state pour les clients
  const [loading, setLoading] = useState(true);

  const [selectedAccount, setSelectedAccount] = useState<number | ''>('');

  const [showOperationForm, setShowOperationForm] = useState(false);
  const [showCheckForm, setShowCheckForm] = useState(false);
  const [showNewAccountModal, setShowNewAccountModal] = useState(false);

  const [newAccount, setNewAccount] = useState({
    nom: '',
    banque: '',
    numero_compte: '',
    type: 'courant' as 'courant' | 'epargne' | 'credit'
  });

  // Ajoute ce state
const [rapprochements, setRapprochements] = useState<RapprochementBancaire[]>([]);

// États pour le formulaire de rapprochement
const [releveForm, setReleveForm] = useState({
  solde_releve: '',
  date_releve: new Date().toISOString().split('T')[0],
  periode_debut: '',
  periode_fin: '',
  observations: ''
});
  const [newOperation, setNewOperation] = useState({
    type: 'virement_recu' as
      | 'virement_recu'
      | 'virement_emis'
      | 'versement_especes'
      | 'retrait_especes'
      | 'cheque_recu'
      | 'cheque_emis',
    description: '',
    amount: '',
    numero_cheque: '',
    date_remise_prevue: '',
    client_id: '' // Changé en client_id pour correspondre au backend (si tu utilises l'ID)
  });

  const [newCheck, setNewCheck] = useState({
    checkNumber: '',
    amount: '',
    client_id: '',
    depositDate: new Date().toISOString().split('T')[0]
  });

  // Chargement des données (comptes, opérations, chèques + clients)
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [comptesRes, opsRes, chequesRes, clientsRes, rapprochementsRes] = await Promise.all([
          getResources(`finance/comptes`),
          getResources(`finance/operations`),
          getResources(`finance/cheques`),
          getResources(CLIENTS_API), // Chargement des clients
          getResources(`finance/rapprochements`) // Nouvelle route à créer côté backend
        ]);

        setComptes(comptesRes.data);
        setOperations(opsRes.data);
        setCheques(chequesRes.data);
        setClients(clientsRes.data.data);
        setRapprochements(rapprochementsRes.data);

        if (comptesRes.data.length > 0) {
          setSelectedAccount(comptesRes.data[0].id);
        }
      } catch (err: any) {
        toast.error('Erreur lors du chargement des données');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

const totalBankBalance = comptes.reduce((sum, c) => sum + c.solde_theorique, 0);
  const selectedAccountData = comptes.find(acc => acc.id === selectedAccount);
  const selectedAccountBalance = selectedAccountData?.solde_theorique || 0;
  const selectedAccountOperations = operations.filter(op =>
    selectedAccount === '' ? true : op.compte_bancaire_id === selectedAccount
  );
  const pendingChecks = cheques.filter(check => check.statut === 'EN_ATTENTE');

  const operationTypes = [
    { value: 'virement_recu', label: 'Virement Reçu', icon: 'ri-arrow-down-line', color: 'text-green-600' },
    { value: 'virement_emis', label: 'Virement Émis', icon: 'ri-arrow-up-line', color: 'text-red-600' },
    { value: 'cheque_recu', label: 'Chèque Reçu', icon: 'ri-file-text-line', color: 'text-blue-600' },
    { value: 'cheque_emis', label: 'Chèque Émis', icon: 'ri-file-text-line', color: 'text-orange-600' },
    { value: 'versement_especes', label: 'Versement Espèces', icon: 'ri-money-euro-circle-line', color: 'text-green-600' },
    { value: 'retrait_especes', label: 'Retrait Espèces', icon: 'ri-money-euro-circle-line', color: 'text-red-600' },
  ];

  const getOperationTypeInfo = (op: OperationBancaire) => {
    const ref = op.reference ?? '';

    // ===== CHÈQUES =====
    if (ref.startsWith('CHQ-IN-')) {
      return operationTypes.find(t => t.value === 'cheque_recu')!;
    }

    if (ref.startsWith('CHQ-OUT-')) {
      return operationTypes.find(t => t.value === 'cheque_emis')!;
    }

    // ===== ESPÈCES =====
    if (ref.startsWith('CASH-IN-')) {
      return operationTypes.find(t => t.value === 'versement_especes')!;
    }

    if (ref.startsWith('CASH-OUT-')) {
      return operationTypes.find(t => t.value === 'retrait_especes')!;
    }

    // ===== VIREMENTS =====
    if (ref.startsWith('VIR-IN-') || op.sens === 'ENTRANT') {
      return operationTypes.find(t => t.value === 'virement_recu')!;
    }

    if (ref.startsWith('VIR-OUT-') || op.sens === 'SORTANT') {
      return operationTypes.find(t => t.value === 'virement_emis')!;
    }

    // ===== FALLBACK SÉCURITÉ =====
    return operationTypes[0];
  };

  const handleAddAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newAccount.nom.trim() || !newAccount.banque.trim() || !newAccount.numero_compte.trim()) {
      toast.error('Tous les champs sont obligatoires');
      return;
    }

    try {
      const res = await createResource(`finance/comptes`, newAccount);
      setComptes([...comptes, res.data]);
      toast.success('Compte bancaire ajouté avec succès');
      setNewAccount({ nom: '', banque: '', numero_compte: '', type: 'courant' });
      setShowNewAccountModal(false);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Erreur lors de l\'ajout du compte');
    }
  };

  const handleOperationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newOperation.amount || parseFloat(newOperation.amount) <= 0) {
      toast.error('Montant invalide');
      return;
    }

    if (['cheque_recu', 'cheque_emis'].includes(newOperation.type) &&
        (!newOperation.numero_cheque || !newOperation.date_remise_prevue)) {
      toast.error('Numéro et date de remise du chèque requis');
      return;
    }

    if (newOperation.type === 'cheque_recu' && !newOperation.client_id) {
      toast.error('Veuillez sélectionner un client');
      return;
    }

    try {
      const payload: any = {
        compte_bancaire_id: selectedAccount || comptes[0]?.id,
        type: newOperation.type,
        montant: parseFloat(newOperation.amount),
        description: newOperation.description.trim() || null,
      };

      if (['cheque_recu', 'cheque_emis'].includes(newOperation.type)) {
        payload.numero_cheque = newOperation.numero_cheque;
        payload.date_remise_prevue = newOperation.date_remise_prevue;
      }

      if (newOperation.type === 'cheque_recu') {
        payload.client_id = Number(newOperation.client_id); // Envoi de l'ID du client
      }

      await createResource(`finance/operations`, payload);

      toast.success('Opération enregistrée');

      const [opsRes, comptesRes] = await Promise.all([
        getResources(`finance/operations`),
        getResources(`finance/comptes`)
      ]);
      setOperations(opsRes.data);
      setComptes(comptesRes.data);

      setNewOperation({
        type: 'virement_recu',
        description: '',
        amount: '',
        numero_cheque: '',
        date_remise_prevue: '',
        client_id: ''
      });
      setShowOperationForm(false);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Erreur lors de l’enregistrement');
    }
  };

  const handleCheckSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCheck.amount || parseFloat(newCheck.amount) <= 0 || !newCheck.checkNumber.trim() || !newCheck.client_id.trim()) {
      toast.error('Tous les champs obligatoires doivent être remplis');
      return;
    }

    try {
      const res = await createResource(`finance/cheques`, {
        compte_bancaire_id: selectedAccount || comptes[0]?.id,
        numero_cheque: newCheck.checkNumber,
        montant: parseFloat(newCheck.amount),
        client_id: newCheck.client_id,
        date_remise_prevue: newCheck.depositDate,
      });

      setCheques([res.data, ...cheques]);
      toast.success('Remise de chèque enregistrée');
      setNewCheck({ checkNumber: '', amount: '', client_id: '', depositDate: new Date().toISOString().split('T')[0] });
      setShowCheckForm(false);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleEncaisserCheque = async (chequeId: number) => {
    if (!confirm('Confirmer l’encaissement de ce chèque ?')) return;

    try {
      await createResource(`finance/cheques/${chequeId}/encaisser`, {});

      toast.success('Chèque encaissé avec succès');

      // Recharger chèques + opérations + comptes
      const [chequesRes, opsRes, comptesRes] = await Promise.all([
        getResources(`finance/cheques`),
        getResources(`finance/operations`),
        getResources(`finance/comptes`)
      ]);

      setCheques(chequesRes.data);
      setOperations(opsRes.data);
      setComptes(comptesRes.data);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Erreur lors de l’encaissement');
    }
  };

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Gestion Bancaire</h2>
            <p className="text-blue-100 mt-1">Suivi des comptes et versements</p>
          </div>
          <div className="text-right">
            <p className="text-blue-100">Solde Total Banques</p>
            <p className="text-3xl font-bold">{totalBankBalance.toFixed(2)} GNF</p>
          </div>
        </div>
      </div>

      {/* Aperçu comptes */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {comptes.map((account) => (
          <div key={account.id} className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="p-3 rounded-full bg-blue-100 text-blue-600">
                  <i className="ri-bank-line text-xl"></i>
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-900">{account.nom}</h3>
                  <p className="text-sm text-gray-500">{account.banque}</p>
                </div>
              </div>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                account.type === 'courant' ? 'bg-blue-100 text-blue-800' :
                account.type === 'epargne' ? 'bg-green-100 text-green-800' :
                account.type === 'credit' ? 'bg-orange-100 text-orange-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {account.type === 'courant' ? 'Courant' : 
                account.type === 'epargne' ? 'Épargne' : 
                account.type === 'credit' ? 'Crédit' : 
                'Type inconnu'}
              </span>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">N° Compte:</span>
                <span className="text-sm font-medium">{account.numero_compte || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Solde Actuel:</span>
                <span className="text-lg font-bold text-gray-900">
                  {account.solde_theorique?.toFixed(2) ?? '0.00'} GNF
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Onglets */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            {['accounts', 'operations', 'checks', 'reconciliation'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab as any)}
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === tab
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <i className={`ri-${tab === 'accounts' ? 'bank' : tab === 'operations' ? 'exchange' : tab === 'checks' ? 'file-text' : 'check-double'}-line mr-2`}></i>
                {tab === 'accounts' ? 'Comptes Bancaires' :
                 tab === 'operations' ? 'Opérations Bancaires' :
                 tab === 'checks' ? 'Remises de Chèques' : 'Rapprochement'}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Onglet Comptes */}
          {activeTab === 'accounts' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">Détails des Comptes Bancaires</h3>
                <div className="flex items-center space-x-4">
                  <select
                    value={selectedAccount}
                    onChange={(e) => setSelectedAccount(e.target.value === '' ? '' : Number(e.target.value))}
                    className="border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="">Tous les comptes</option>
                    {comptes.map(acc => (
                      <option key={acc.id} value={acc.id}>{acc.nom} - {acc.banque}</option>
                    ))}
                  </select>
                  <button onClick={() => setShowNewAccountModal(true)} className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700">
                    <i className="ri-add-line mr-2"></i>Nouveau Compte
                  </button>
                </div>
              </div>

              {/* Dernières opérations du compte sélectionné */}
              <div>
                <h4 className="text-lg font-medium text-gray-900 mb-4">Dernières Opérations</h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Montant</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Référence</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {selectedAccountOperations.slice(0, 10).map((op) => {
                        const typeInfo = getOperationTypeInfo(op);
                        return (
                          <tr key={op.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {new Date(op.date_virement).toLocaleDateString('fr-FR')}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="inline-flex items-center">
                                <i className={`${typeInfo.icon} mr-2 ${typeInfo.color}`}></i>
                                {typeInfo.label}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900">{op.description || '-'}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <span className={op.sens === 'ENTRANT' ? 'text-green-600' : 'text-red-600'}>
                                {op.sens === 'ENTRANT' ? '+' : '-'}{op.montant.toFixed(2)} GNF
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500">{op.reference || '-'}</td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                op.statut === 'EXECUTE' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {op.statut === 'EXECUTE' ? 'Exécuté' : 'En cours'}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* === OPÉRATIONS === */}
          {activeTab === 'operations' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">Opérations Bancaires</h3>
              <div className="flex items-center space-x-4">
                <select
                  value={selectedAccount}
                  onChange={(e) => setSelectedAccount(e.target.value === '' ? '' : Number(e.target.value))}
                  className="border border-gray-300 rounded-md px-3 py-2"
                >
                  <option value="">Tous les comptes</option>
                  {comptes.map(acc => (
                    <option key={acc.id} value={acc.id}>{acc.nom} ({acc.banque})</option>
                  ))}
                </select>
                <button
                  onClick={() => setShowOperationForm(true)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                >
                  <i className="ri-add-line mr-2"></i>Nouvelle Opération
                </button>
              </div>
            </div>

            {/* Formulaire Nouvelle Opération avec SELECT pour client */}
            {showOperationForm && (
              <div className="bg-blue-50 rounded-lg p-6">
                <h4 className="text-lg font-medium text-blue-900 mb-4">Nouvelle Opération Bancaire</h4>
                <form onSubmit={handleOperationSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Type d'Opération</label>
                      <select
                        value={newOperation.type}
                        onChange={(e) => setNewOperation({ ...newOperation, type: e.target.value as any })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2"
                        required
                      >
                        {operationTypes.map(type => (
                          <option key={type.value} value={type.value}>{type.label}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Montant (GNF)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={newOperation.amount}
                        onChange={(e) => setNewOperation({ ...newOperation, amount: e.target.value })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2"
                        required
                      />
                    </div>

                    {['cheque_recu', 'cheque_emis'].includes(newOperation.type) && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Numéro de chèque</label>
                          <input
                            type="text"
                            value={newOperation.numero_cheque}
                            onChange={(e) => setNewOperation({ ...newOperation, numero_cheque: e.target.value })}
                            className="w-full border border-gray-300 rounded-md px-3 py-2"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Date de remise prévue</label>
                          <input
                            type="date"
                            value={newOperation.date_remise_prevue}
                            onChange={(e) => setNewOperation({ ...newOperation, date_remise_prevue: e.target.value })}
                            className="w-full border border-gray-300 rounded-md px-3 py-2"
                            required
                          />
                        </div>

                        {/* Nouveau : Select pour le client si chèque reçu */}
                        {newOperation.type === 'cheque_recu' && (
                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Client émetteur du chèque *
                            </label>
                            <select
                              value={newOperation.client_id}
                              onChange={(e) => setNewOperation({ ...newOperation, client_id: e.target.value })}
                              className="w-full border border-gray-300 rounded-md px-3 py-2"
                              required
                            >
                              <option value="">Sélectionner un client</option>
                              {clients.map(client => (
                                <option key={client.id} value={client.id}>
                                  {client.raison_sociale}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                    <textarea
                      value={newOperation.description}
                      onChange={(e) => setNewOperation({ ...newOperation, description: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      rows={3}
                    />
                  </div>

                  <div className="flex justify-end space-x-3">
                    <button type="button" onClick={() => setShowOperationForm(false)} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">
                      Annuler
                    </button>
                    <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                      Enregistrer
                    </button>
                  </div>
                </form>
              </div>
            )}

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Montant</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Référence</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {selectedAccountOperations.map((op) => {
                      const typeInfo = getOperationTypeInfo(op);
                      return (
                        <tr key={op.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {new Date(op.date_virement).toLocaleDateString('fr-FR')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center">
                              <i className={`${typeInfo.icon} mr-2 ${typeInfo.color}`}></i>
                              {typeInfo.label}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">{op.description || '-'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <span className={op.sens === 'ENTRANT' ? 'text-green-600' : 'text-red-600'}>
                              {op.sens === 'ENTRANT' ? '+' : '-'}{op.montant.toFixed(2)} GNF
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">{op.reference || '-'}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              op.statut === 'EXECUTE' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {op.statut === 'EXECUTE' ? 'Exécuté' : 'En cours'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* === REMISES DE CHÈQUES === */}
          {activeTab === 'checks' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">Remises de Chèques</h3>
                <button
                  onClick={() => setShowCheckForm(true)}
                  className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 whitespace-nowrap"
                >
                  <i className="ri-add-line mr-2"></i>
                  Nouvelle Remise
                </button>
              </div>

              {pendingChecks.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <i className="ri-alert-line text-yellow-600 mr-2 text-lg"></i>
                    <span className="text-sm font-medium text-yellow-800">
                      {pendingChecks.length} chèque(s) en attente d'encaissement
                    </span>
                  </div>
                </div>
              )}

              {showCheckForm && (
                <div className="bg-green-50 rounded-lg p-6">
                  <h4 className="text-lg font-medium text-green-900 mb-4">Nouvelle Remise de Chèque</h4>
                  <form onSubmit={handleCheckSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Compte de Dépôt</label>
                        <select
                          value={selectedAccount}
                          onChange={(e) => setSelectedAccount(Number(e.target.value))}
                          className="w-full border border-gray-300 rounded-md px-3 py-2"
                          required
                        >
                          {comptes.map(account => (
                            <option key={account.id} value={account.id}>
                              {account.nom} - {account.banque}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Numéro de Chèque</label>
                        <input
                          type="text"
                          value={newCheck.checkNumber}
                          onChange={(e) => setNewCheck({ ...newCheck, checkNumber: e.target.value })}
                          className="w-full border border-gray-300 rounded-md px-3 py-2"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Montant (GNF)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={newCheck.amount}
                          onChange={(e) => setNewCheck({ ...newCheck, amount: e.target.value })}
                          className="w-full border border-gray-300 rounded-md px-3 py-2"
                          required
                        />
                      </div>
                      {/* Nouveau : Select pour le client si chèque reçu */}
                         <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Client émetteur du chèque *
                            </label>
                            <select
                              value={newCheck.client_id}
                              onChange={(e) => setNewCheck({ ...newCheck, client_id: e.target.value })}
                              className="w-full border border-gray-300 rounded-md px-3 py-2"
                              required
                            >
                              <option value="">Sélectionner un client</option>
                              {clients.map(client => (
                                <option key={client.id} value={client.id}>
                                  {client.raison_sociale}
                                </option>
                              ))}
                            </select>
                          </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Date de Remise Prévue</label>
                        <input
                          type="date"
                          value={newCheck.depositDate}
                          onChange={(e) => setNewCheck({ ...newCheck, depositDate: e.target.value })}
                          className="w-full border border-gray-300 rounded-md px-3 py-2"
                          required
                        />
                      </div>
                    </div>
                    <div className="flex justify-end space-x-3">
                      <button type="button" onClick={() => setShowCheckForm(false)} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">
                        Annuler
                      </button>
                      <button type="submit" className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700">
                        Enregistrer
                      </button>
                    </div>
                  </form>
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">N° Chèque</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Montant</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date Remise</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {cheques.map((check) => (
                      <tr key={check.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{check.numero_cheque}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{check.client?.raison_sociale}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {Number(check.montant).toFixed(2)} GNF
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(check.date_remise_prevue).toLocaleDateString('fr-FR')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            check.statut === 'ENCAISSE' ? 'bg-green-100 text-green-800' :
                            check.statut === 'EN_ATTENTE' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {check.statut === 'ENCAISSE' ? 'Encaissé' :
                             check.statut === 'EN_ATTENTE' ? 'En attente' : 'Rejeté'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {check.statut === 'EN_ATTENTE' && (
                            <button
                              onClick={() => handleEncaisserCheque(check.id)}
                              className="bg-blue-600 text-white px-3 py-1 rounded-md text-xs hover:bg-blue-700"
                            >
                              <i className="ri-check-line mr-1"></i>
                              Encaisser
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* === RAPPROCHEMENT === */}
          {activeTab === 'reconciliation' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">Rapprochement Bancaire</h3>
                <div className="flex items-center space-x-4">
                  <select
                    value={selectedAccount}
                    onChange={(e) => setSelectedAccount(Number(e.target.value))}
                    className="border border-gray-300 rounded-md px-3 py-2"
                  >
                    {comptes.map(account => (
                      <option key={account.id} value={account.id}>
                        {account.nom} - {account.banque}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Formulaire de rapprochement */}
              <div className="bg-white rounded-lg shadow p-6">
                <h4 className="text-lg font-medium text-gray-900 mb-6">Nouveau Rapprochement</h4>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  if (!selectedAccount) {
                    toast.error('Veuillez sélectionner un compte');
                    return;
                  }

                  try {
                    const payload = {
                      compte_bancaire_id: selectedAccount,
                      periode_debut: releveForm.periode_debut,
                      periode_fin: releveForm.periode_fin || releveForm.date_releve,
                      solde_releve: parseFloat(releveForm.solde_releve),
                      observations: releveForm.observations.trim() || null
                    };

                    const res = await createResource(`finance/rapprochements`, payload);

                    toast.success('Rapprochement enregistré avec succès');
                    setRapprochements([res.data, ...rapprochements]);

                    // Reset formulaire
                    setReleveForm({
                      solde_releve: '',
                      date_releve: new Date().toISOString().split('T')[0],
                      periode_debut: '',
                      periode_fin: '',
                      observations: ''
                    });
                  } catch (err: any) {
                    toast.error(err.response?.data?.message || 'Erreur lors de l\'enregistrement');
                  }
                }} className="grid grid-cols-1 md:grid-cols-2 gap-6">

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Période de début
                    </label>
                    <input
                      type="date"
                      value={releveForm.periode_debut}
                      onChange={(e) => setReleveForm({ ...releveForm, periode_debut: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Période de fin (ou date du relevé)
                    </label>
                    <input
                      type="date"
                      value={releveForm.periode_fin || releveForm.date_releve}
                      onChange={(e) => setReleveForm({ ...releveForm, periode_fin: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Solde théorique (système)
                    </label>
                    <div className="w-full bg-gray-100 border border-gray-300 rounded-md px-3 py-2 text-gray-900 font-medium">
                      {selectedAccountBalance.toFixed(2)} GNF
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Solde selon relevé bancaire
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={releveForm.solde_releve}
                      onChange={(e) => setReleveForm({ ...releveForm, solde_releve: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      placeholder="Ex: 1 250 000.00"
                      required
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Écart calculé
                    </label>
                    <div className={`text-2xl font-bold p-4 rounded-lg text-center ${
                      parseFloat(releveForm.solde_releve || '0') - selectedAccountBalance === 0
                        ? 'bg-green-100 text-green-800'
                        : Math.abs(parseFloat(releveForm.solde_releve || '0') - selectedAccountBalance) > 0.01
                        ? 'bg-red-100 text-red-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {(parseFloat(releveForm.solde_releve || '0') - selectedAccountBalance).toFixed(2)} GNF
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Observations (facultatif)
                    </label>
                    <textarea
                      value={releveForm.observations}
                      onChange={(e) => setReleveForm({ ...releveForm, observations: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      rows={3}
                      placeholder="Ex: Écart dû à un chèque en attente d'encaissement..."
                    />
                  </div>

                  <div className="md:col-span-2 flex justify-end">
                    <button
                      type="submit"
                      className="bg-green-600 text-white px-6 py-3 rounded-md hover:bg-green-700 font-medium"
                    >
                      <i className="ri-check-double-line mr-2"></i>
                      Enregistrer le Rapprochement
                    </button>
                  </div>
                </form>
              </div>

              {/* Historique des rapprochements */}
              <div>
                <h4 className="text-lg font-medium text-gray-900 mb-4">Historique des Rapprochements</h4>
                {rapprochements
                  .filter(r => r.compte_bancaire_id === selectedAccount)
                  .length === 0 ? (
                  <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                    <p>Aucun rapprochement enregistré pour ce compte</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Période</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Solde Théorique</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Solde Relevé</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Écart</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Observations</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {rapprochements
                          .filter(r => r.compte_bancaire_id === selectedAccount)
                          .sort((a, b) => new Date(b.periode_fin).getTime() - new Date(a.periode_fin).getTime())
                          .map((r) => (
                            <tr key={r.id}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {new Date(r.periode_debut).toLocaleDateString('fr-FR')} → {new Date(r.periode_fin).toLocaleDateString('fr-FR')}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-900">{r.solde_theorique.toFixed(2)} GNF</td>
                              <td className="px-6 py-4 text-sm text-gray-900">{r.solde_releve.toFixed(2)} GNF</td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`font-medium ${r.ecart === 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {r.ecart > 0 ? '+' : ''}{r.ecart.toFixed(2)} GNF
                                </span>
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-500">
                                {new Date(r.created_at).toLocaleDateString('fr-FR')}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-600">
                                {r.observations || '-'}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ================= MODAL NOUVEAU COMPTE ================= */}
      {showNewAccountModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">Nouveau Compte Bancaire</h3>
                <button
                  onClick={() => setShowNewAccountModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
            </div>

            <form onSubmit={handleAddAccount} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom du Compte *
                </label>
                <input
                  type="text"
                  value={newAccount.nom}
                  onChange={(e) => setNewAccount({ ...newAccount, nom: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  placeholder="Ex: Compte Principal BGFI"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Banque *
                </label>
                <input
                  type="text"
                  value={newAccount.banque}
                  onChange={(e) => setNewAccount({ ...newAccount, banque: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  placeholder="Ex: BGFI Bank"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Numéro de Compte *
                </label>
                <input
                  type="text"
                  value={newAccount.numero_compte}
                  onChange={(e) => setNewAccount({ ...newAccount, numero_compte: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  placeholder="Ex: 00123456789"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type de Compte *
                </label>
                <select
                  value={newAccount.type}
                  onChange={(e) => setNewAccount({ ...newAccount, type: e.target.value as any })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="courant">Compte Courant</option>
                  <option value="epargne">Compte Épargne</option>
                  <option value="credit">Compte Crédit</option>
                </select>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowNewAccountModal(false)}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  <i className="ri-check-line mr-2"></i>
                  Ajouter le Compte
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}